// src/pages/Inventory/InventorySubTablePage.tsx
// GENERAL
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

// COMPONENTS
import Section from "../../../components/layout/Section/Section";
import GroupModal from "../view/groupBymodal/GroupModal";
import BaseButton from "../../../components/ui/BaseButton/BaseButton";
import BaseText from "../../../components/ui/BaseText/BaseText";
import { PrimaryTable } from "../../../components/ui/Tables";
import {
	RefreshButton,
	ResetFiltersButton,
} from "../../../components/ui/Tables/PrimaryTable";

// UTILS && HELPERS
import getFontAwesomeIcon from "../../../utils/getFontAwesomeIcon";
import { createApplyGrouping } from "../helpers";

// HOOKS
import { PageDataProvider } from "../../../hooks/usePageDataContext";
import { DataTableContextProvider } from "../../../components/ui/Tables/hooks/useDataTableContext";
import usePageListDetails from "../../../hooks/usePageListDetails";
import { useTableStore } from "../../../stores";

// FIELDS
import { groupOptions, inventorySubTablePageFields } from "../viewModel";
// TYPES
import {
	TableDisplayData,
	AdditionalData,
} from "../../../components/ui/Tables/types";

function capitalizeFirstLetter(str: string) {
	return str.charAt(0).toUpperCase() + str.slice(1);
}

const InventorySubTablePage: React.FC = () => {
	// DEFAULT TABLE VIEW
	const defaultOption =
		groupOptions.find((o) => o.isDefault) || groupOptions[0];
	const defaultOptionUuid = defaultOption.uuid;
	const pageSlug = defaultOption.pageSlugs.subtablePageSlug as string;
	const tableSlug = defaultOption.tableSlugs.primaryTableSlug;
	const pageTitle = "Inventory";
	const paramName = `${tableSlug}:group`;
	const { pageMeta } = usePageListDetails("inventory");

	const { state } = useLocation() as {
		state?: { hasSubTable: boolean };
	};
	const hasSubTable = state?.hasSubTable ?? false;

	const navigate = useNavigate();
	const { setSlug } = useTableStore();
	const { search, pathname } = useLocation();
	const params = new URLSearchParams(search);
	const [searchParams] = useSearchParams();

	const unitsSubtableParentItemUuid = searchParams.get(
		"inventory_items:sales-orders-item-uuid"
	);

	let selectedPurchaseOrderItemUuid = unitsSubtableParentItemUuid;
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [resetKey, setResetKey] = useState<number>(0);
	const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
	const [tableRefresh, setTableRefresh] = useState<boolean>(false);
	const [additionalData, setAdditionalData] = useState<AdditionalData>(
		{} as AdditionalData
	);
	const [tableDisplayData, setTableDisplayData] = useState<TableDisplayData>(
		{} as TableDisplayData
	);
	const [isAdditionalDataLoaded, setIsAdditionalDataLoaded] =
		useState<boolean>(false);
	const [appliedGroup, setAppliedGroup] = useState<string>(tableSlug);
	const [pendingGroup, setPendingGroup] = useState<string>(appliedGroup);

	// PAGE SETTINGS VIA DUMMY FIELDS
	const isGroupByModalVisible =
		inventorySubTablePageFields.page.isGroupByModalVisible;
	const isResetFiltersButtonVisible =
		inventorySubTablePageFields.page.isResetFiltersButtonVisible;

	const applyGrouping = createApplyGrouping(
		navigate,
		pathname,
		search,
		paramName,
		groupOptions,
		setAppliedGroup,
		setPendingGroup,
		setIsModalOpen
	);

	const openModal = () => {
		setPendingGroup(appliedGroup);
		setIsModalOpen(true);
	};

	useEffect(() => {
		let additionalData: AdditionalData = { salesOrderStageId: undefined };

		// VIEW: Customer POs By Items
		if (currentTableView.uuid == "904cc7b1-ad50-426b-b64f-87bb23d0a93a") {
			setTableDisplayData(
				currentTableView.tableDisplayData as TableDisplayData
			);
		} else if (
			//VIEW: Items By Customer POs
			currentTableView.uuid == "94f1e621-6eb4-bece-7f67-302ed49bce88"
		) {
			setTableDisplayData(
				currentTableView.tableDisplayData as TableDisplayData
			);
		}
		setAdditionalData(additionalData);

		setIsAdditionalDataLoaded(true);

		// also ensure setSlug is called once (you can do this here or in its own effect)
		setSlug(pageSlug);
	}, [search, setSlug]);

	const [backUrl, setBackUrl] = useState<string>("/inventory");
	useEffect(() => {
		const stored = sessionStorage.getItem("inventoryLastUrl");
		if (stored) setBackUrl(stored);
	}, []);

	const handleTableRefresh = () => {
		setResetKey((prevKey) => prevKey + 1);
		setTableRefresh((prev) => !prev);
	};

	const currentTableView = groupOptions.find(
		(groupOption) => groupOption.isDefault
	)!;
	const currentGroupViewOptionsButtonText = `${currentTableView.label}`;

	const returnToSelectItemsButtonClasses =
		"text-supply-blue-500 hover:text-supply-blue-300 pt-2";

	return (
		<div className="px-[60px] h-full font-omnes-regular ">
			<Section additionalClasses="py-6">
				<div className="flex items-center justify-between">
					<div className="flex flex-col items-start">
						<div className="flex items-center">
							{getFontAwesomeIcon("boxesStacked", "solid")}
							<BaseText
								text={capitalizeFirstLetter(pageTitle)}
								additionalClasses="pl-2"
								size="text-22px"
								color="text-navy-500"
								tag="h1"
								fontFamily="font-omnes-medium"
							/>
						</div>
						<BaseButton
							text={"Back"}
							fontColor="supply-blue-500"
							additionalClasses={returnToSelectItemsButtonClasses}
							icon={getFontAwesomeIcon("chevronLeft", "regular")}
							iconClasses="text-sm pr-2"
							fontFamily="font-omnes-regular"
							as="link"
							to={backUrl}
						/>
					</div>
					<div className="flex ">
						{isGroupByModalVisible && (
							<div className="mx-2 ">
								<BaseButton
									text={currentGroupViewOptionsButtonText}
									bgColor={"bg-white"}
									fontColor="text-navy-500"
									buttonSize="h-26px"
									shape="rounded"
									icon={
										<span className="text-white bg-primary rounded text-xs p-0.5">
											{getFontAwesomeIcon(
												"layerGroup",
												"solid"
											)}
										</span>
									}
									iconBreakpoint="max-sm:pr-0"
									additionalClasses="shadow-filter-group hover:bg-supply-blue-50 border-1 border-navy-200 text-nowrap max-sm:size-8 max-sm:justify-center text-14px px-2 py-1"
									as="button"
									textBreakpoint="max-sm:hidden"
									onClick={openModal}
									fontFamily="font-omnes-medium"
									iconClasses="pr-2"
								/>
							</div>
						)}
						{/* <div className="h-8 w-[1px] bg-navy-250 "></div> */}
						<div className="mx-2 flex gap-4">
							<RefreshButton
								loading={loading}
								onClick={handleTableRefresh}
							/>
							{isResetFiltersButtonVisible && (
								<ResetFiltersButton slug={pageSlug} />
							)}
						</div>
					</div>
				</div>
			</Section>
			{isAdditionalDataLoaded && (
				<PageDataProvider pageMeta={pageMeta} pageTitle={pageTitle}>
					<DataTableContextProvider
						showStickyColumn
						hasInfiniteScroll={tableDisplayData?.hasInfiniteScroll}
						hasModalTable={false}
						data={[]}
						key={resetKey}
						isNestedChildTable={false}
						rowsHaveModalOnClick={false}
						isSubTable={true}
					>
						<PrimaryTable
							pageTitle={pageTitle}
							hasModalTable={false}
							setPageTitle={() => {}}
							setHasModalTable={() => {}}
							setData={() => {}}
							additionalData={additionalData}
							slug={tableSlug}
							setIsTableLoading={setLoading}
							selectedPurchaseOrderItemUuid={
								selectedPurchaseOrderItemUuid as string
							}
							isTableSearchAndSortEnabled={false}
							tableDisplayData={
								tableDisplayData.subTableParent as TableDisplayData
							}
						/>
					</DataTableContextProvider>
				</PageDataProvider>
			)}
			{isGroupByModalVisible && (
				<GroupModal
					isOpen={isModalOpen}
					onClose={() => setIsModalOpen(false)}
					groupOptions={groupOptions}
					selectedValue={pendingGroup}
					onGroupChange={setPendingGroup}
					onApply={applyGrouping}
					viewSlug={tableSlug}
				/>
			)}
		</div>
	);
};

export default InventorySubTablePage;
